const DEFAULT_CATEGORY_LABEL = 'Necategorizat';
const DEFAULT_OTHER_CATEGORY_LABEL = 'Altele';
const DEFAULT_OTHER_CATEGORY_PATH = 'altele';
const DEFAULT_STATUS_LABEL = 'Necunoscut';
const SELF_SELECTION_PREFIX = '__self__:';

const STATUS_LABELS = {
  bidding: 'In ofertare',
  hidden: 'Ascuns',
  cannot_bid: 'Nu poate fi ofertat',
  offered: 'Ofertat',
  submitted: 'Trimisa',
  pending: 'In asteptare',
  accepted: 'Acceptata',
  rejected: 'Respinsa',
  won: 'Castigata',
  lost: 'Pierduta',
  canceled: 'Anulata',
  cancelled: 'Anulata'
};
const STATUS_TAG_COLORS = {
  bidding: 'processing',
  hidden: 'default',
  cannot_bid: 'error',
  offered: 'success',
  submitted: 'success',
  pending: 'warning',
  accepted: 'success',
  rejected: 'error',
  won: 'success',
  lost: 'volcano',
  canceled: 'default',
  cancelled: 'default'
};

const normalizeText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
};

const normalizeCategoryPath = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value)
    .trim()
    .replace(/\\/g, '/')
    .replace(/\s*\/\s*/g, '/')
    .replace(/^\/+|\/+$/g, '')
    .replace(/\/{2,}/g, '/');
};

const formatPathSegmentToLabel = (pathSegment) => String(pathSegment || '')
  .replace(/[-_]+/g, ' ')
  .replace(/\s{2,}/g, ' ')
  .trim();

const extractCategoryFromObject = (candidate) => {
  if (!candidate) {
    return '';
  }

  if (typeof candidate === 'string') {
    return candidate;
  }

  if (Array.isArray(candidate)) {
    for (const value of candidate) {
      const extractedValue = extractCategoryFromObject(value);
      if (extractedValue) {
        return extractedValue;
      }
    }
    return '';
  }

  const nestedKeys = ['name', 'title', 'label', 'value'];
  for (const key of nestedKeys) {
    if (candidate[key]) {
      return String(candidate[key]);
    }
  }

  return '';
};

const buildCategoryMaps = (payload) => {
  const categoryLabelMap = new Map();
  const categoryPathMap = new Map();
  const payloadList = Array.isArray(payload) ? payload : [payload];

  const collectCategorySources = (payloadEntry) => {
    const directSources = [
      payloadEntry?.categories,
      payloadEntry?.product_categories,
      payloadEntry?.necessity_list_categories,
      payloadEntry?.necessity_list?.categories
    ];
    const screenSources = Object.values(payloadEntry?.categories_by_screen || {});
    return [...directSources, ...screenSources];
  };

  payloadList.forEach((payloadEntry) => {
    if (!payloadEntry) return;
    const categorySources = collectCategorySources(payloadEntry);

    categorySources.forEach((source) => {
      if (!Array.isArray(source)) return;
      source.forEach((categoryEntry) => {
        const categoryId = categoryEntry?.id ?? categoryEntry?.category_id;
        const categoryLabel = extractCategoryFromObject(
          categoryEntry?.name ?? categoryEntry?.label ?? categoryEntry?.title
        );
        const categoryPath = normalizeCategoryPath(
          categoryEntry?.full_path ??
          categoryEntry?.fullPath ??
          categoryEntry?.path ??
          categoryLabel
        );

        if ((categoryId === 0 || categoryId) && categoryLabel) {
          categoryLabelMap.set(String(categoryId), categoryLabel.trim());
        }

        if ((categoryId === 0 || categoryId) && categoryPath) {
          categoryPathMap.set(String(categoryId), categoryPath);
        }
      });
    });
  });

  return { categoryLabelMap, categoryPathMap };
};

const resolveCategoryLabelFromItem = (item, categoryLabelMap = new Map()) => {
  const product = item?.product || item?.originalData?.product || {};
  const directLabelCandidates = [
    item?.category,
    item?.category_name,
    item?.categoryName,
    product?.category_name,
    product?.categoryName,
    product?.category
  ];

  for (const directLabelCandidate of directLabelCandidates) {
    const extractedLabel = extractCategoryFromObject(directLabelCandidate);
    if (extractedLabel && normalizeText(extractedLabel)) {
      return extractedLabel.trim();
    }
  }

  const categoryIdCandidates = [
    item?.category_id,
    item?.categoryId,
    item?.product_category_id,
    item?.product_category?.id,
    product?.category_id,
    product?.categoryId,
    product?.category?.id,
    product?.product_category_id,
    product?.product_category?.id
  ];

  for (const categoryIdCandidate of categoryIdCandidates) {
    if (categoryIdCandidate !== 0 && !categoryIdCandidate) continue;
    const mappedLabel = categoryLabelMap.get(String(categoryIdCandidate));
    if (mappedLabel) {
      return mappedLabel;
    }
  }

  return DEFAULT_CATEGORY_LABEL;
};

const resolveCategoryPathFromItem = (
  item,
  categoryPathMap = new Map(),
  categoryLabelMap = new Map()
) => {
  const product = item?.product || item?.originalData?.product || {};
  const directPathCandidates = [
    item?.category_path,
    item?.categoryPath,
    item?.full_path,
    item?.fullPath,
    item?.category?.full_path,
    item?.category?.fullPath,
    product?.category_path,
    product?.categoryPath,
    product?.full_path,
    product?.fullPath,
    product?.category?.full_path,
    product?.category?.fullPath
  ];

  for (const directPathCandidate of directPathCandidates) {
    const normalizedPath = normalizeCategoryPath(directPathCandidate);
    if (normalizedPath) {
      return normalizedPath;
    }
  }

  const categoryIdCandidates = [
    item?.category_id,
    item?.categoryId,
    item?.product_category_id,
    item?.product_category?.id,
    product?.category_id,
    product?.categoryId,
    product?.category?.id,
    product?.product_category_id,
    product?.product_category?.id
  ];

  for (const categoryIdCandidate of categoryIdCandidates) {
    if (categoryIdCandidate !== 0 && !categoryIdCandidate) continue;
    const mappedPath = categoryPathMap.get(String(categoryIdCandidate));
    if (mappedPath) {
      return mappedPath;
    }
  }

  const fallbackLabel = resolveCategoryLabelFromItem(item, categoryLabelMap);
  const normalizedFallbackPath = normalizeCategoryPath(fallbackLabel);
  return normalizedFallbackPath || DEFAULT_OTHER_CATEGORY_PATH;
};

export const createBackendCategoryResolver = (payload) => {
  const { categoryLabelMap } = buildCategoryMaps(payload);
  return (item) => resolveCategoryLabelFromItem(item, categoryLabelMap);
};

export const createBackendCategoryPathResolver = (payload) => {
  const { categoryLabelMap, categoryPathMap } = buildCategoryMaps(payload);
  return (item) => resolveCategoryPathFromItem(item, categoryPathMap, categoryLabelMap);
};

const normalizeApiCategoryOptions = (categoryEntries) => {
  if (!Array.isArray(categoryEntries)) return [];

  return categoryEntries
    .map((categoryEntry) => {
      const categoryName = extractCategoryFromObject(
        categoryEntry?.name ?? categoryEntry?.label ?? categoryEntry?.title
      );
      const categoryPath = normalizeCategoryPath(
        categoryEntry?.full_path ??
        categoryEntry?.fullPath ??
        categoryEntry?.path ??
        categoryName
      );
      const categoryId = categoryEntry?.id ?? categoryEntry?.category_id ?? categoryName;
      const count = categoryEntry?.count ?? categoryEntry?.items_count ?? categoryEntry?.products_count;
      if ((categoryId === 0 || categoryId) && categoryName) {
        return {
          value: categoryPath || String(categoryName).trim(),
          label: String(categoryName).trim(),
          fullPath: categoryPath || String(categoryName).trim(),
          count: Number.isFinite(Number(count)) ? Number(count) : undefined
        };
      }
      return null;
    })
    .filter(Boolean);
};

export const buildCategoryOptionsFromEndpoint = (payload, preferredKeys = []) => {
  const roots = [payload, payload?.data, payload?.categories_by_screen, payload?.screens].filter(Boolean);

  for (const root of roots) {
    if (Array.isArray(root)) {
      const options = normalizeApiCategoryOptions(root);
      if (options.length) return options;
    }

    for (const key of preferredKeys) {
      const options = normalizeApiCategoryOptions(root?.[key]);
      if (options.length) return options;
    }
  }

  return [];
};

export const mergeCategoryOptionsWithProducts = (categoryOptions, products) => {
  const mergedOptionsByPath = new Map();

  const ensureEntry = (fullPath, label) => {
    if (!fullPath) return null;
    if (!mergedOptionsByPath.has(fullPath)) {
      mergedOptionsByPath.set(fullPath, {
        value: fullPath,
        fullPath,
        label: label || formatPathSegmentToLabel(fullPath.split('/').pop()),
        count: 0
      });
    }
    return mergedOptionsByPath.get(fullPath);
  };

  if (Array.isArray(categoryOptions)) {
    categoryOptions.forEach((categoryOption) => {
      const fullPath = normalizeCategoryPath(
        categoryOption?.fullPath ?? categoryOption?.value ?? categoryOption?.label
      );
      if (!fullPath) return;
      const normalizedLabel = String(categoryOption?.label || '').trim();
      ensureEntry(fullPath, normalizedLabel || formatPathSegmentToLabel(fullPath.split('/').pop()));
    });
  }

  if (Array.isArray(products)) {
    products.forEach((product) => {
      const rawPath = normalizeCategoryPath(
        product?.category_path ??
        product?.categoryPath ??
        product?.full_path ??
        product?.fullPath
      );
      const fullPath = rawPath || DEFAULT_OTHER_CATEGORY_PATH;
      const fallbackLabel = fullPath === DEFAULT_OTHER_CATEGORY_PATH
        ? DEFAULT_OTHER_CATEGORY_LABEL
        : formatPathSegmentToLabel(fullPath.split('/').pop());
      const entry = ensureEntry(fullPath, fallbackLabel);
      if (!entry) return;
      entry.count += 1;
    });
  }

  return Array.from(mergedOptionsByPath.values());
};

export const filterProductsBySearchAndCategories = (items, rawSearchQuery, selectedCategories) => {
  const normalizedSearchQuery = normalizeText(rawSearchQuery);
  const normalizeSelectedCategoryValue = (selectedCategory) => {
    const rawValue = String(selectedCategory || '').trim();
    if (!rawValue) return '';
    if (rawValue === 'all') return 'all';
    if (rawValue.startsWith(SELF_SELECTION_PREFIX)) {
      const ownPath = normalizeCategoryPath(rawValue.slice(SELF_SELECTION_PREFIX.length)).toLowerCase();
      return ownPath ? `${SELF_SELECTION_PREFIX}${ownPath}` : '';
    }
    return normalizeCategoryPath(rawValue).toLowerCase();
  };
  const normalizedSelectedCategories = Array.isArray(selectedCategories)
    ? selectedCategories.map((category) => normalizeSelectedCategoryValue(category)).filter(Boolean)
    : [];
  const hasAllCategoriesSelected =
    normalizedSelectedCategories.length === 0 || normalizedSelectedCategories.includes('all');

  return items.filter((item) => {
    const categoryLabel = item?.category || resolveCategoryLabelFromItem(item);
    const categoryPath = normalizeCategoryPath(
      item?.category_path ??
      item?.categoryPath ??
      item?.full_path ??
      item?.fullPath ??
      categoryLabel
    ).toLowerCase();
    const normalizedCategoryLabel = normalizeText(categoryLabel);
    const isCategoryMatch =
      hasAllCategoriesSelected || normalizedSelectedCategories.some((selectedCategory) => {
        if (selectedCategory.startsWith(SELF_SELECTION_PREFIX)) {
          const selectedOwnPath = selectedCategory.slice(SELF_SELECTION_PREFIX.length);
          return categoryPath === selectedOwnPath;
        }

        const normalizedSelectedPath = selectedCategory;
        return (
          normalizedCategoryLabel === selectedCategory ||
          categoryPath === normalizedSelectedPath ||
          categoryPath.startsWith(`${normalizedSelectedPath}/`)
        );
      });

    if (!isCategoryMatch) {
      return false;
    }

    if (!normalizedSearchQuery) {
      return true;
    }

    const searchableFields = [
      item?.name,
      item?.barcode,
      item?.instructions,
      item?.originalData?.supplementary_info,
      categoryLabel
    ];

    return searchableFields.some((fieldValue) => normalizeText(fieldValue).includes(normalizedSearchQuery));
  });
};

export const resolveStatusLabel = (rawStatus, fallbackLabel = DEFAULT_STATUS_LABEL) => {
  const normalizedStatus = normalizeText(rawStatus);

  if (!normalizedStatus) {
    return fallbackLabel;
  }

  if (STATUS_LABELS[normalizedStatus]) {
    return STATUS_LABELS[normalizedStatus];
  }

  const humanReadableStatus = String(rawStatus)
    .replace(/_/g, ' ')
    .trim();

  if (!humanReadableStatus) {
    return fallbackLabel;
  }

  return humanReadableStatus.charAt(0).toUpperCase() + humanReadableStatus.slice(1);
};

export const resolveStatusTagProps = (rawStatus, fallbackLabel = DEFAULT_STATUS_LABEL) => {
  const normalizedStatus = normalizeText(rawStatus);

  return {
    label: resolveStatusLabel(rawStatus, fallbackLabel),
    color: STATUS_TAG_COLORS[normalizedStatus] || 'default'
  };
};
